import React, {
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { FormattedMessage } from 'react-intl';

import { stripesConnect } from '@folio/stripes/core';

import TeamForm from '../../components/team/team-form';
import { useCallout } from '../../../hooks';
import {
  TEAM_FIELDS,
  TEAM_INITIAL_VALUES,
  getTeamListUrl,
  getTeamViewUrl,
  STATUS_CODE_DUPLICATE,
} from '../../../constants';
import {
  getCreateEditTeamPayload,
} from '../../../utils';
import { IfPermissionWrapper } from '../../../components/common';

const TeamCreateRoute = ({
  mutator,
  history,
  location,
}) => {
  const showCallout = useCallout();

  const initialValues = {
    [TEAM_FIELDS.NAME]: TEAM_INITIAL_VALUES[TEAM_FIELDS.NAME],
    [TEAM_FIELDS.DESCRIPTION]: TEAM_INITIAL_VALUES[TEAM_FIELDS.DESCRIPTION],
    [TEAM_FIELDS.MEMBERS]: TEAM_INITIAL_VALUES[TEAM_FIELDS.MEMBERS],
  };

  const onBack = useCallback(() => {
    history.push({
      pathname: getTeamListUrl(),
      search: location.search,
    });
  }, [history, location.search]);

  const onSubmit = useCallback((values) => {
    const requestBody = getCreateEditTeamPayload(values);

    mutator.createTeam.POST({ ...requestBody })
      .then((response) => {
        showCallout({
          message: <FormattedMessage
            id="ui-task-list.callout.created.team"
            values={{ name: response[TEAM_FIELDS.NAME] }}
          />,
        });
        history.push({
          pathname: getTeamViewUrl(response[TEAM_FIELDS.ID]),
          search: location.search,
        });
      })
      .catch((error) => {
        let calloutMessageId = 'validation.communicationProblem';

        if (error.status === STATUS_CODE_DUPLICATE) {
          calloutMessageId = 'error.duplicate.team';
        }

        showCallout({
          type: 'error',
          message: <FormattedMessage id={`ui-task-list.${calloutMessageId}`} />,
        });
      });
  }, [showCallout, location.search, history, mutator.createTeam]);

  const getTitle = () => <FormattedMessage id="ui-task-list.title.new.team" />;

  return (
    <IfPermissionWrapper perm="ui-task-list.teams.create-edit">
      <TeamForm
        formTitle={getTitle}
        initialValues={initialValues}
        onBack={onBack}
        onSubmit={onSubmit}
      />
    </IfPermissionWrapper>
  );
};

TeamCreateRoute.manifest = Object.freeze({
  createTeam: {
    type: 'okapi',
    path: 'tasks/teams',
    clientGeneratePk: false,
    throwErrors: false,
    fetch: false,
  },
});

TeamCreateRoute.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  mutator: PropTypes.shape({
    createTeam: PropTypes.shape({
      POST: PropTypes.func.isRequired,
    }),
  }),
};

export default stripesConnect(TeamCreateRoute);
